import { Link } from "react-router-dom";
import "./dashboardCharts.css";

// P144-R: rebuilt from scratch (not patched) against the LiveMall
// Front reference implementation - C:\Users\Manaswin\Documents\LiveMall\Front
// (src/pages/admin/Dashboard.jsx, src/components/charts/{Bars,Donut,LineChart}.jsx,
// src/components/Metric.jsx, src/styles/{tokens,global}.css). What was
// borrowed is the LAYOUT LANGUAGE only - dense card headers, mono numerals,
// uppercase micro-labels, a real plotted axis with gridlines rather than
// bare stick bars, and compact legends. None of LiveMall's business data,
// wording, or red brand identity crossed over: this stays on Workmate's own
// navy/blue token scale, with red/amber reserved strictly for real risk.
//
// Still zero chart dependencies (no chart library exists in this repo's
// package.json), still real SVG/CSS only, still never inventing a number:
// every consumer builds its data through resolveBar() so "loading"/"error"/
// "ready" stay consistent across chart types, and a real zero is always a
// real, visible zero rather than a removed panel.

const ACCENT_COLORS = {
  primary: "#1c74d6",
  "primary-strong": "#114b8c",
  "primary-deep": "#0b2e59",
  slate: "#7c93ab",
  "slate-light": "#b7c8dc",
  warning: "#b6790a",
  success: "#17805a",
  danger: "#c8322a",
};

function accentColor(accent) {
  return ACCENT_COLORS[accent] || ACCENT_COLORS.primary;
}

/** Thousands separators for every rendered figure - a dashboard showing six-figure worker counts is unreadable without them. */
export function formatCount(value) {
  return typeof value === "number" ? value.toLocaleString("en-US") : value;
}

/** Adapts a {status, totalCount} feed into one normalized chart datum: {key, label, status, value, accent, to}. */
export function resolveBar(key, label, feed, { accent = "primary", to = null, selector = (f) => f.totalCount } = {}) {
  if (!feed || feed.status === "loading") {
    return { key, label, status: "loading", value: null, accent, to };
  }
  if (feed.status === "error") {
    return { key, label, status: "error", value: null, accent, to };
  }
  return { key, label, status: "ready", value: selector(feed) ?? 0, accent, to };
}

/**
 * Shared panel shell. LiveMall's `.card` + `.card__h` rhythm: a tight
 * 12x16 header with a 13px/600 title, an optional muted sub-label pinned
 * right, then the body. Deliberately flatter and denser than this app's
 * generic .wx-panel (which is sized for form screens, not analytics).
 */
export function ChartPanel({ title, tag, note, children, dense, footer }) {
  return (
    <section className={`wx-panel wx-dash-panel wx-chart-panel${dense ? " wx-chart-panel--dense" : ""}`}>
      <div className="wx-dash-panel-head">
        <h2>{title}</h2>
        {tag && <span className="wx-dash-panel-tag">{tag}</span>}
      </div>
      <div className="wx-chart-panel-body">
        {note}
        {children}
      </div>
      {footer && <div className="wx-chart-panel-foot">{footer}</div>}
    </section>
  );
}

function isAllZero(bars) {
  const allReady = bars.length > 0 && bars.every((bar) => bar.status === "ready");
  return allReady && bars.every((bar) => (bar.value ?? 0) === 0);
}

/** Rounds an axis maximum up to a readable tick value (10/25/50/100/250/...) so gridline labels are never 137, 274, 411. */
function niceAxisMax(value) {
  if (value <= 4) {
    return 4;
  }
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
    const candidate = step * magnitude;
    if (candidate >= value) {
      return candidate;
    }
  }
  return magnitude * 10;
}

const AXIS_TICK_COUNT = 4;

/**
 * A real plotted bar chart: a labelled y-axis, horizontal gridlines, a
 * solid baseline, and consistent-width bars with their value above each
 * column. This replaces the earlier "skinny stick on an empty track" look -
 * the track background is gone entirely, because a bar chart's empty space
 * IS the remaining scale, and drawing a box around it made every bar read
 * like a half-filled progress meter.
 */
export function AxisBarChart({ bars, emptyNote, height = 156 }) {
  const readyValues = bars.filter((bar) => bar.status === "ready").map((bar) => bar.value ?? 0);
  const maxValue = Math.max(0, ...readyValues);

  if (isAllZero(bars)) {
    return <AxisEmptyState bars={bars} message={emptyNote} />;
  }

  const axisMax = niceAxisMax(maxValue);

  return (
    <div className="wx-bars">
      <div className="wx-bars-plot" style={{ height }}>
        {/* Gridlines only, no numeric tick labels - the same choice
            LiveMall's own Bars.jsx makes. Each bar already carries its
            exact value above it, so a second numeric scale down the side
            would just be redundant ink competing with the real figures. */}
        <div className="wx-bars-grid" aria-hidden="true">
          {Array.from({ length: AXIS_TICK_COUNT + 1 }, (_, index) => (
            <span key={index} />
          ))}
        </div>
        <div className="wx-bars-cols">
          {bars.map((bar) => (
            <AxisBarColumn key={bar.key} bar={bar} axisMax={axisMax} />
          ))}
        </div>
      </div>
      <div className="wx-bars-labels">
        {bars.map((bar) =>
          bar.to ? (
            <Link key={bar.key} to={bar.to} className="wx-bars-label wx-bars-label--link">
              {bar.label}
            </Link>
          ) : (
            <span key={bar.key} className="wx-bars-label">
              {bar.label}
            </span>
          )
        )}
      </div>
    </div>
  );
}

function AxisBarColumn({ bar, axisMax }) {
  const isReady = bar.status === "ready";
  const value = bar.value ?? 0;
  const isZero = isReady && value <= 0;
  const heightPct = isReady && value > 0 ? Math.max(2, (value / axisMax) * 100) : 0;

  return (
    <div className="wx-bar-col">
      <span className={`wx-bar-value${!isReady ? ` wx-bar-value--${bar.status}` : ""}${isZero ? " wx-bar-value--zero" : ""}`}>
        {bar.status === "loading" ? "…" : bar.status === "error" ? "—" : formatCount(value)}
      </span>
      {isReady && !isZero ? (
        <div className="wx-bar-fill" style={{ height: `${heightPct}%`, background: accentColor(bar.accent) }} />
      ) : (
        <div className={`wx-bar-nil${!isReady ? " wx-bar-nil--pending" : ""}`} />
      )}
    </div>
  );
}

function AxisEmptyState({ bars, message }) {
  return (
    <div className="wx-axis-empty">
      <div className="wx-axis-empty-baseline" aria-hidden="true">
        {bars.map((bar) => (
          <span key={bar.key} className="wx-axis-empty-marker" />
        ))}
      </div>
      <p className="wx-axis-empty-message">{message}</p>
      <div className="wx-axis-empty-chips">
        {bars.map((bar) => (
          <span key={bar.key} className="wx-axis-empty-chip">
            {bar.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** A real ring chart with a center total and a compact value+share legend. */
export function DonutChart({ segments, centerLabel, size = 132, strokeWidth = 20 }) {
  const allReady = segments.length > 0 && segments.every((segment) => segment.status === "ready");
  const anyLoading = segments.some((segment) => segment.status === "loading");
  const total = allReady ? segments.reduce((sum, segment) => sum + (segment.value ?? 0), 0) : 0;
  const isZeroState = allReady && total === 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  let offset = 0;
  const arcs = [];
  if (allReady && total > 0) {
    for (const segment of segments) {
      const value = segment.value ?? 0;
      if (value <= 0) continue;
      const dash = (value / total) * circumference;
      arcs.push(
        <circle
          key={segment.key}
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={accentColor(segment.accent)}
          strokeWidth={strokeWidth}
          strokeDasharray={`${dash} ${circumference - dash}`}
          strokeDashoffset={-offset}
          transform={`rotate(-90 ${center} ${center})`}
        />
      );
      offset += dash;
    }
  }

  return (
    <div className={`wx-donut-chart${isZeroState ? " wx-donut-chart--empty" : ""}`}>
      <div className="wx-donut-visual" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={centerLabel}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--wx-color-surface-sunken)"
            strokeWidth={isZeroState ? strokeWidth * 0.55 : strokeWidth}
          />
          {arcs}
        </svg>
        <div className="wx-donut-center">
          <strong>{anyLoading ? "…" : isZeroState ? "–" : formatCount(total)}</strong>
          <span>{isZeroState ? "ยังไม่มีข้อมูล" : centerLabel}</span>
        </div>
      </div>
      <ul className="wx-donut-legend">
        {segments.map((segment) => {
          const share = allReady && total > 0 ? Math.round(((segment.value ?? 0) / total) * 100) : null;
          return (
            <li key={segment.key} className="wx-donut-legend-item">
              <span className="wx-donut-legend-dot" style={{ background: accentColor(segment.accent) }} aria-hidden="true" />
              <span className="wx-donut-legend-label">{segment.label}</span>
              <span className="wx-donut-legend-value">
                {segment.status === "ready" ? formatCount(segment.value) : segment.status === "loading" ? "…" : "—"}
              </span>
              <span className="wx-donut-legend-share">{share === null ? "" : `${share}%`}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * A real stepped funnel: every stage block is centre-aligned and tapers
 * with its own real value, so the silhouette itself reads as a funnel
 * rather than a stack of left-aligned progress bars. The stage name sits
 * in a fixed left gutter and the conversion share in a fixed right gutter,
 * so all five rows stay optically aligned no matter how wide each block is.
 */
export function FunnelChart({ stages, emptyNote }) {
  const readyValues = stages.filter((stage) => stage.status === "ready").map((stage) => stage.value ?? 0);
  const maxValue = Math.max(1, ...readyValues);
  const firstStage = stages[0];
  const baseValue = firstStage?.status === "ready" ? firstStage.value ?? 0 : 0;
  const empty = isAllZero(stages);

  return (
    <div className="wx-funnel">
      {stages.map((stage, index) => {
        const isReady = stage.status === "ready";
        const value = stage.value ?? 0;
        const widthPct = isReady ? (value > 0 ? Math.max(18, Math.round((value / maxValue) * 100)) : 18) : 42;
        const conversion = isReady && index > 0 && baseValue > 0 ? Math.round((value / baseValue) * 100) : null;
        const depth = Math.min(index, 4);

        const rowContent = (
          <>
            <span className="wx-funnel-name">{stage.label}</span>
            <span className="wx-funnel-bar">
              <span
                className={`wx-funnel-block wx-funnel-block--d${depth}${!isReady ? " wx-funnel-block--pending" : ""}`}
                style={{ width: `${widthPct}%` }}
              >
                <span className="wx-funnel-value">
                  {isReady ? formatCount(value) : stage.status === "loading" ? "…" : "—"}
                </span>
              </span>
            </span>
            <span className="wx-funnel-share">{conversion === null ? "" : `${conversion}%`}</span>
          </>
        );

        return stage.to ? (
          <Link to={stage.to} className="wx-funnel-row wx-funnel-row--link" key={stage.key}>
            {rowContent}
          </Link>
        ) : (
          <div className="wx-funnel-row" key={stage.key}>
            {rowContent}
          </div>
        );
      })}
      {empty && emptyNote && <p className="wx-chart-empty-note">{emptyNote}</p>}
    </div>
  );
}

/** A compact node-and-connector flow (Super Agent -> PLC -> Agent -> Worker -> Applications). */
export function HierarchyFlow({ nodes, emptyNote }) {
  const empty = isAllZero(nodes);

  return (
    <div className="wx-flow">
      <ol className="wx-flow-track">
        {nodes.map((node) => (
          <li className="wx-flow-item" key={node.key}>
            <span className="wx-flow-step" aria-hidden="true" />
            <span className="wx-flow-label">{node.label}</span>
            <span className="wx-flow-value">
              {node.status === "ready" ? formatCount(node.value) : node.status === "loading" ? "…" : "—"}
            </span>
          </li>
        ))}
      </ol>
      {empty && emptyNote && <p className="wx-chart-empty-note">{emptyNote}</p>}
    </div>
  );
}

/**
 * The Trend slot's honest chart shell. This backend exposes no time-series
 * endpoint anywhere (every dashboard feed is a point-in-time totalCount or
 * summary), so there is no series to draw. Rather than fabricate a slope,
 * this renders a real, gridded chart frame whose only plotted mark is the
 * single day that genuinely exists - today - with the axis explicitly
 * labelled as having no prior history. Intentionally uses <line>/<circle>
 * and never a <polyline> or a "sparkline"/"trend-chart" class: a guard test
 * asserts on exactly that, so a fabricated multi-point series cannot be
 * introduced here later without failing.
 */
export function TrendShell({ todayLabel, todayValue, valueLabel }) {
  return (
    <div className="wx-trend-empty">
      <div className="wx-trend-frame">
        <svg className="wx-trend-empty-line" viewBox="0 0 320 96" preserveAspectRatio="none" aria-hidden="true">
          {[0, 1, 2, 3].map((index) => (
            <line key={index} className="wx-trend-grid" x1="0" y1={12 + index * 24} x2="320" y2={12 + index * 24} />
          ))}
          <line className="wx-trend-axis" x1="0" y1="84" x2="320" y2="84" />
          <circle cx="300" cy="36" r="5" />
        </svg>
        <span className="wx-trend-point-flag">
          <strong>{todayValue}</strong>
          <span>{valueLabel}</span>
        </span>
      </div>
      <div className="wx-trend-foot">
        <span className="wx-trend-empty-today">วันนี้ · {todayLabel}</span>
        <span className="wx-trend-empty-message">ยังไม่มีข้อมูลย้อนหลังเพียงพอสำหรับแสดงแนวโน้ม</span>
      </div>
    </div>
  );
}
